import { Trash } from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import type { MeterImportRowErrors } from "@/lib/schemas/meter-import.schema";
import { METER_IMPORT_TYPES } from "@/lib/schemas/meter-import.schema";
import { cn } from "@/lib/utils";
import type { MeterImportRow } from "./import-meters-types";

interface ImportMetersEditorRowProps {
	row: MeterImportRow;
	errors: MeterImportRowErrors;
	isDuplicateSerial: boolean;
	onChange: (patch: Partial<MeterImportRow>) => void;
	onDelete: () => void;
}

const ERROR_KEY = "border-destructive focus-visible:ring-destructive/40";

export function ImportMetersEditorRow({
	row,
	errors,
	isDuplicateSerial,
	onChange,
	onDelete,
}: ImportMetersEditorRowProps) {
	const { t } = useTranslation();
	const serialError =
		errors.serial_number || (isDuplicateSerial ? "duplicate" : null);
	return (
		<tr className="border-b">
			<td className="p-1.5 align-top">
				<Input
					aria-label={t("meterImport.col.serial_number")}
					value={row.serial_number}
					onChange={(e) => onChange({ serial_number: e.target.value })}
					className={cn("h-8", serialError && ERROR_KEY)}
					title={
						serialError ? t(`meterImport.rowError.${serialError}`) : undefined
					}
				/>
			</td>
			<td className="p-1.5 align-top">
				<Input
					aria-label={t("meterImport.col.encryption_key")}
					value={row.encryption_key}
					onChange={(e) => onChange({ encryption_key: e.target.value })}
					className="h-8"
				/>
			</td>
			<td className="p-1.5 align-top">
				<Select
					value={row.type || undefined}
					onValueChange={(v) => onChange({ type: v })}
				>
					<SelectTrigger
						className={cn("h-8 w-full", errors.type && ERROR_KEY)}
						aria-label={t("meterImport.col.type")}
					>
						<SelectValue placeholder="—" />
					</SelectTrigger>
					<SelectContent>
						{METER_IMPORT_TYPES.map((v) => (
							<SelectItem key={v} value={v}>
								{v}
							</SelectItem>
						))}
					</SelectContent>
				</Select>
			</td>
			<td className="p-1.5 align-top">
				<Input
					aria-label={t("meterImport.col.street")}
					value={row.street}
					onChange={(e) => onChange({ street: e.target.value })}
					className={cn("h-8", errors.street && ERROR_KEY)}
					title={
						errors.street
							? t(`meterImport.rowError.${errors.street}`)
							: undefined
					}
				/>
			</td>
			<td className="p-1.5 align-top">
				<Input
					aria-label={t("meterImport.col.apartment_number")}
					value={row.apartment_number}
					onChange={(e) => onChange({ apartment_number: e.target.value })}
					className="h-8"
				/>
			</td>
			<td className="p-1.5 align-top">
				<Button
					type="button"
					variant="ghost"
					size="icon"
					onClick={onDelete}
					aria-label={t("meterImport.deleteRow")}
				>
					<Trash aria-hidden="true" className="size-4" />
				</Button>
			</td>
		</tr>
	);
}
